import {GetStaticPathsResult, GetStaticProps} from 'next';
import {Text, Flex, Box, Center, Spinner} from '@chakra-ui/react';
import {useMemo, useState, useEffect} from 'react';
import {NextSeo} from 'next-seo';
import {ResultOf} from '@graphql-typed-document-node/core';
import {
  UserCollectionOverviewQuery,
  UserCollectionOverviewDocument,
  PublicCollectionOverview,
  AssetOverview,
  useUserQuery,
} from '../../graphql/generated';
import {client} from '../../graphql/client';
import {PageLayout} from '../../components/global/page-layout';
import {CollectionPageDocument} from '../../graphql/datocms-generated';
import {
  CollectionCard,
  CollectionCardProps,
} from '../../components/showpiece-card/collection-card';
import {request} from '../../graphql/api';

interface CollectionProps {
  countryCode: string;
  userCollectionOverview: PublicCollectionOverview;
  assetData: ResultOf<typeof CollectionPageDocument>['allAssetModels'];
}

const Collection: React.FC<CollectionProps> = ({
  userCollectionOverview,
  assetData,
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const {userFirstName, assetOverviews, signature} = userCollectionOverview;
  const userQuery = useUserQuery();
  // TODO: REFER
  // const {currency, currencyPostfix} = useGeolocation();

  const purchasedAssets = useMemo(() => {
    if (assetOverviews) {
      const sortedOverviews = [...assetOverviews].sort(
        (a: AssetOverview, b: AssetOverview) =>
          // eslint-disable-next-line radix
          parseInt(b.recentPurchaseDate) - parseInt(a.recentPurchaseDate),
      );
      const sortedPurchasedAssets = sortedOverviews.map(
        (overview: AssetOverview) => overview.assetId,
      );
      return sortedPurchasedAssets;
    }
    return [];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assetOverviews]);

  useEffect(() => {
    const timeOut = setTimeout(() => {
      setIsLoading(false);
    }, 1000);
    // eslint-disable-next-line consistent-return
    return () => {
      if (timeOut) clearTimeout(timeOut);
    };
  }, []);

  return (
    <>
      <NextSeo noindex />
      <PageLayout
        firstName={userQuery.data?.user?.firstName}
        hideShapes
        additionalContentStyles={{
          pt: 6,
        }}
      >
        {userFirstName && (
          <>
            {/* TODO: REFER */}
            {/* <Text
              pb={8}
              textStyle="body4"
              fontWeight={400}
              lineHeight="14px"
              textAlign="center"
            >
              {`Ask ${userFirstName} to `}
              <Link href="/refer-a-friend">
                <a
                  style={{
                    color: theme.colors.aqua['500'],
                    textDecoration: 'underline',
                  }}
                >
                  Refer you as a friend
                </a>
              </Link>{' '}
              for {currency}5{currencyPostfix} off your next purchase
            </Text> */}
            <Text textAlign="center" textStyle="h4">
              {`${userFirstName}'s Public Collection`}
            </Text>
          </>
        )}
        <Box position="relative">
          <Center
            py={56}
            pos="absolute"
            top="0"
            left="0"
            right="0"
            opacity={isLoading ? 1 : 0}
            transition="opacity .3s ease"
            zIndex="1"
          >
            <Spinner color="aqua.500" size="lg" />
          </Center>

          <Flex
            pos="relative"
            zIndex="2"
            opacity={isLoading ? 0 : 1}
            transition="opacity .3s ease"
            flexWrap="wrap"
            justifyContent="center"
            alignItems="stretch"
            mt={10}
          >
            {purchasedAssets &&
              purchasedAssets.map((id: string) => {
                const asset = assetData.find(
                  (assetInfo) => assetInfo.assetId === id,
                );
                const currentAsset = assetOverviews?.find(
                  ({assetId}) => assetId === id,
                );

                if (!asset) return null;
                return (
                  <Box
                    flexGrow={0}
                    flexShrink={1}
                    flexBasis="380px"
                    width="100%"
                    min-width="300px"
                    max-width="400px"
                    p={4}
                  >
                    <CollectionCard
                      {...(asset as CollectionCardProps)}
                      isPublic
                      totalPieces={currentAsset?.totalPiecesAvailable || 0}
                      isDisabled={currentAsset?.totalPiecesAvailable === 0}
                      momentOfDelightType={asset.momentOfDelight as string}
                      displaySide={currentAsset?.displayedSide || 0}
                      usersSignature={signature}
                    />
                  </Box>
                );
              })}
            {assetData &&
              assetData.map((asset) => {
                const isAvaliable = purchasedAssets.includes(
                  asset.assetId || '',
                );
                if (!isAvaliable && asset.cardTitle && asset.cardType)
                  return (
                    <Box
                      flexGrow={0}
                      flexShrink={1}
                      flexBasis="380px"
                      width="100%"
                      min-width="300px"
                      max-width="400px"
                      p={4}
                    >
                      <CollectionCard
                        {...(asset as CollectionCardProps)}
                        isPublic
                        isDisabled
                      />
                    </Box>
                  );
                return <></>;
              })}
          </Flex>
        </Box>
      </PageLayout>
    </>
  );
};

export const getStaticPaths = (): GetStaticPathsResult => {
  return {
    fallback: 'blocking',
    paths: [],
  };
};

export const getStaticProps: GetStaticProps<any, {userId: string}> = async (
  props,
) => {
  // get all errors including GraphQL and Apollo errors
  const {data, errors, error} = await client.query<UserCollectionOverviewQuery>(
    {
      query: UserCollectionOverviewDocument,
      variables: {
        userId: props.params?.userId,
      },
      errorPolicy: 'all',
    },
  );
  const req = await request(CollectionPageDocument);
  return {
    revalidate: 30,
    notFound: error || errors || !data?.userCollectionOverview,
    props: {
      assetData: req.allAssetModels,
      userCollectionOverview: data?.userCollectionOverview,
    },
  };
};

export default Collection;
